import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "manager-notification-view-drawer__close-trigger" }
const _hoisted_2 = {
  key: 0,
  class: "manager-notification-view-drawer__loading"
}
const _hoisted_3 = { class: "manager-notification-view-drawer__meta" }
const _hoisted_4 = { class: "field-with-label" }
const _hoisted_5 = { class: "field-with-label__value" }
const _hoisted_6 = { class: "field-with-label" }
const _hoisted_7 = { class: "field-with-label__value" }
const _hoisted_8 = { class: "field-with-label" }
const _hoisted_9 = { class: "manager-notification-view-drawer__message" }
const _hoisted_10 = { class: "field-with-label" }
const _hoisted_11 = { class: "manager-notification-view-drawer__message-text" }
const _hoisted_12 = { key: 1 }
const _hoisted_13 = {
  key: 0,
  class: "manager-notification-view-drawer__files"
}
const _hoisted_14 = { class: "manager-notification-view-drawer__file-list" }
const _hoisted_15 = ["src"]
const _hoisted_16 = { class: "manager-notification-view-drawer__file-content" }
const _hoisted_17 = ["href"]
const _hoisted_18 = { key: 0 }
const _hoisted_19 = {
  key: 1,
  class: "manager-notification-view-drawer__reply"
}
const _hoisted_20 = { class: "manager-notification-view-drawer__reply-list" }
const _hoisted_21 = { class: "field-with-label" }
const _hoisted_22 = { class: "field-with-label__value" }
const _hoisted_23 = { class: "field-with-label" }
const _hoisted_24 = { class: "field-with-label__value manager-notification-view-drawer__reply-text" }

import { reactive, ref, watch } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import ContentTagsHtml from '@/js/components/content-tags/content-tags-html.vue';
import { getDocFormatIconPath, getFileFormatLabel } from '@/js/utils/file-upload-utils';
import {
  getManagerNotificationStatusLabel,
  getManagerNotificationStatusModifier,
  normalizeNotificationFiles,
  normalizeNotificationStatusCode,
} from '../resident-notifications/map-notification';
import type { NotificationFile } from '../resident-notifications/types';
import type { ManagerNotificationListItem } from './manager-notification-api';
import { isManagerNotificationsMockEnabled } from './is-manager-notifications-mock';

type NotificationReplyItem = {
  id?: number;
  createdAt?: string;
  text?: string;
  managerReply?: boolean;
  files?: unknown[];
};

type ResidentReplyViewItem = {
  id: number;
  date: string;
  text: string;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'NotificationViewDrawer',
  props: {
    open: { type: Boolean },
    message: {}
  },
  emits: ["update:open", "marked-read"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const loading = ref(false);

const detail = reactive({
  subject: '',
  date: '',
  status: '',
  statusModifier: 'read',
  message: '',
  residentReplies: [] as ResidentReplyViewItem[],
  files: [] as NotificationFile[],
});

watch(
  () => [props.open, props.message] as const,
  ([isOpen, message]) => {
    if (!isOpen || !message) {
      return;
    }

    void loadNotification(message);
  },
);

function formatDrawerDate(iso: string): string {
  if (!iso) return '';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  const datePart = date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return `${datePart} / ${timePart}`;
}

function handleClose(): void {
  emit('update:open', false);
}

function formatFileMeta(file: NotificationFile): string {
  const type = file.type?.trim() || getFileFormatLabel(file.name);
  const size = file.size?.trim();

  return [type, size].filter(Boolean).join(' / ');
}

function mapResidentReplies(replies: unknown): ResidentReplyViewItem[] {
  if (!Array.isArray(replies) || replies.length === 0) {
    return [];
  }

  return replies
    .filter((reply): reply is NotificationReplyItem => {
      return (
        Boolean(reply) &&
        typeof reply === 'object' &&
        (reply as NotificationReplyItem).managerReply === false
      );
    })
    .sort((left, right) => {
      const leftTime = left.createdAt ? new Date(left.createdAt).getTime() : 0;
      const rightTime = right.createdAt ? new Date(right.createdAt).getTime() : 0;
      return leftTime - rightTime;
    })
    .map((reply, index) => ({
      id: reply.id ?? index,
      date: formatDrawerDate(reply.createdAt ?? ''),
      text: reply.text?.trim() ?? '',
    }))
    .filter((reply) => reply.date || reply.text);
}

function applyNotificationStatus(status?: string | null): void {
  const statusCode = normalizeNotificationStatusCode(status);
  detail.statusModifier = getManagerNotificationStatusModifier(statusCode);
  detail.status = getManagerNotificationStatusLabel(statusCode);
}

function applyListMessage(message: ManagerNotificationListItem): void {
  detail.subject = message.subject ?? '';
  detail.date = formatDrawerDate(message.createdAt);
  applyNotificationStatus(message.status);
  detail.message = message.text ?? '';
  detail.residentReplies = mapResidentReplies(message.replies);
  detail.files = normalizeNotificationFiles(message.files);
}

async function loadNotification(message: ManagerNotificationListItem): Promise<void> {
  applyListMessage(message);

  if (isManagerNotificationsMockEnabled()) {
    loading.value = false;
    return;
  }

  loading.value = true;

  try {
    const { fetchManagerNotificationDetail, markManagerNotificationRead } =
      await import('./manager-notification-api');

    const [data] = await Promise.all([
      fetchManagerNotificationDetail(message.id),
      markManagerNotificationRead(message.id),
    ]);

    detail.subject = data.subject ?? detail.subject;
    detail.date = data.createdAt ? formatDrawerDate(data.createdAt) : detail.date;
    applyNotificationStatus(data.status ?? message.status);
    detail.message = data.message ?? detail.message;
    detail.files = normalizeNotificationFiles(data.files);

    emit('marked-read');
  } catch (error) {
    console.error('Ошибка загрузки уведомления:', error);
  } finally {
    loading.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  const _component_a_drawer = _resolveComponent("a-drawer")!

  return (_openBlock(), _createBlock(_component_a_drawer, {
    width: 720,
    open: __props.open,
    class: "manager-notification-view-drawer",
    "body-style": { padding: '32px' },
    footer: null,
    "destroy-on-close": "",
    onClose: handleClose
  }, {
    title: _withCtx(() => [...(_cache[0] || (_cache[0] = [
      _createElementVNode("span", { class: "manager-notification-view-drawer__title" }, "Уведомление", -1)
    ]))]),
    closeIcon: _withCtx(() => [
      _createElementVNode("span", _hoisted_1, [
        _createVNode(_unref(CloseOutlined))
      ])
    ]),
    default: _withCtx(() => [
      (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, "Загрузка..."))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createElementVNode("section", _hoisted_3, [
              _createElementVNode("div", _hoisted_4, [
                _cache[1] || (_cache[1] = _createElementVNode("span", { class: "field-with-label__label" }, "Тема", -1)),
                _createElementVNode("span", _hoisted_5, _toDisplayString(detail.subject || '—'), 1)
              ]),
              _createElementVNode("div", _hoisted_6, [
                _cache[2] || (_cache[2] = _createElementVNode("span", { class: "field-with-label__label" }, "Дата / время", -1)),
                _createElementVNode("span", _hoisted_7, _toDisplayString(detail.date || '—'), 1)
              ]),
              _createElementVNode("div", _hoisted_8, [
                _cache[4] || (_cache[4] = _createElementVNode("span", { class: "field-with-label__label" }, "Статус", -1)),
                _createElementVNode("span", {
                  class: _normalizeClass(["manager-notification-view-drawer__status", `manager-notification-view-drawer__status--${detail.statusModifier}`])
                }, [
                  _cache[3] || (_cache[3] = _createElementVNode("span", {
                    class: "manager-notification-view-drawer__status-dot",
                    "aria-hidden": "true"
                  }, null, -1)),
                  _createTextVNode(" " + _toDisplayString(detail.status), 1)
                ], 2)
              ])
            ]),
            _createElementVNode("section", _hoisted_9, [
              _createElementVNode("div", _hoisted_10, [
                _cache[5] || (_cache[5] = _createElementVNode("span", { class: "field-with-label__label" }, "Уведомление", -1)),
                _createElementVNode("div", _hoisted_11, [
                  (detail.message)
                    ? (_openBlock(), _createBlock(ContentTagsHtml, {
                        key: 0,
                        html: detail.message
                      }, null, 8, ["html"]))
                    : (_openBlock(), _createElementBlock("p", _hoisted_12, "—"))
                ])
              ])
            ]),
            (detail.files.length)
              ? (_openBlock(), _createElementBlock("section", _hoisted_13, [
                  _cache[6] || (_cache[6] = _createElementVNode("span", { class: "field-with-label__label" }, "Прикреплённые файлы", -1)),
                  _createElementVNode("ul", _hoisted_14, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(detail.files, (file, index) => {
                      return (_openBlock(), _createElementBlock("li", {
                        key: `${file.url}-${file.name}-${index}`,
                        class: "manager-notification-view-drawer__file-item"
                      }, [
                        _createElementVNode("img", {
                          src: _unref(getDocFormatIconPath)(file.name),
                          alt: "",
                          class: "manager-notification-view-drawer__file-icon"
                        }, null, 8, _hoisted_15),
                        _createElementVNode("div", _hoisted_16, [
                          _createElementVNode("a", {
                            href: file.url,
                            target: "_blank",
                            rel: "noopener noreferrer",
                            download: ""
                          }, _toDisplayString(file.name), 9, _hoisted_17),
                          (formatFileMeta(file))
                            ? (_openBlock(), _createElementBlock("span", _hoisted_18, _toDisplayString(formatFileMeta(file)), 1))
                            : _createCommentVNode("", true)
                        ])
                      ]))
                    }), 128))
                  ])
                ]))
              : _createCommentVNode("", true),
            (detail.residentReplies.length)
              ? (_openBlock(), _createElementBlock("section", _hoisted_19, [
                  _cache[9] || (_cache[9] = _createElementVNode("span", { class: "field-with-label__label" }, "Ответ резидента", -1)),
                  _createElementVNode("div", _hoisted_20, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(detail.residentReplies, (reply) => {
                      return (_openBlock(), _createElementBlock("article", {
                        key: reply.id,
                        class: "manager-notification-view-drawer__reply-item"
                      }, [
                        _createElementVNode("div", _hoisted_21, [
                          _cache[7] || (_cache[7] = _createElementVNode("span", { class: "field-with-label__label" }, "Дата", -1)),
                          _createElementVNode("span", _hoisted_22, _toDisplayString(reply.date || '—'), 1)
                        ]),
                        _createElementVNode("div", _hoisted_23, [
                          _cache[8] || (_cache[8] = _createElementVNode("span", { class: "field-with-label__label" }, "Ответ", -1)),
                          _createElementVNode("span", _hoisted_24, _toDisplayString(reply.text || '—'), 1)
                        ])
                      ]))
                    }), 128))
                  ])
                ]))
              : _createCommentVNode("", true)
          ], 64))
    ]),
    _: 1
  }, 8, ["open"]))
}
}

})